import { apiCall, getApiUrl, apiCallWithManySymbols } from './util/apiCall';
import { getQuotesFromSymbolCache, symbolCacheStoreResult } from './util/storage';
import { mergeIntoUndefinedSlots, keyResponseDataBySymbol, toCsv } from './util/util.js';

/**
 * <h1>Synacor CloudQuote API SDK</h1>
 * The public API for interfacing with CloudQuote
 * <p>Two layers of caching:</p>
 *   <p>1. Cache requests by URL to fetchCache<p>
 *   <p>2. Cache symbols found in response to symbolCache<p>
 * @module CloudQuote
 */

/**
 * Returns a matrix of currencies
 * @function getCurrencyMatrix
 * @param {String} currencies  a list of currencies
 * @returns {Promise}          a Promise resolving with JSON or rejecting with error
 * @memberof CloudQuote
 */
export function getCurrencyMatrix(currencies) {
	return apiCall('/fcon/getCurrencyMatrix.json', { currencies });
}

/**
 * Get a list of currency prices
 * @function getCurrencyPrice
 * @param {String|String[]} symbol     a symbol or array of symbols
 * @returns {Promise}         a Promise resolving with JSON or rejecting with error
 * @memberof CloudQuote
 */
export function getCurrencyPrice(symbol) {
	symbol = toCsv(symbol);
	return apiCall('/fcon/getCurrencyPrice.json', { symbol })
		.then(symbolCacheStoreResult);
}

/**
 * Run a query and get back a list of quotes
 * @function findStock
 * @param {String} search     A CloudQuote API query string
 * @memberof CloudQuote
 */
export function findStock(search) {
	return apiCall('/fcon/findStock.json', { sort: 'Popularity', search })
		.then(symbolCacheStoreResult);
}

/**
 * Get a list of stock quotes
 * @function getStockPrice
 * @param {String|String[]} symbol    a symbol or array of symbols
 * @returns {Promise}                 a Promise resolving with JSON or rejecting with error
 * @memberof CloudQuote
 */
export function getStockPrice(symbol) {
	const { results, miss } = getQuotesFromSymbolCache(symbol);
	if (!miss.length) { // if all symbols are cached we can avoid a network request entirely
		return Promise.resolve(results);
	}

	symbol = toCsv(miss);

	return apiCall('/fcon/getStockPrice.json', { symbol })
		.then(symbolCacheStoreResult)
		.then((data) => (
			results && results.rows.length
				? { rows: mergeIntoUndefinedSlots(results.rows, data.rows) }
				: data
		));
}

/**
 * Get the history of prices on a stock symbol
 * @function getStockPriceHistory
 * @param {Object|String} params      an object with parameters to be passed to the CloudQuote API. if passed a string, the string will be used as the only parameter "symbol"
 * @param {String} [params.symbol]    a symbol
 * @param {String} [params.from]      a date string for when historical data should begin, format 'YYYY-MM-DD'
 * @param {String} [params.to]        a date string for when historical data should end, format 'YYYY-MM-DD'
 * @returns {Promise}                 a Promise resolving with JSON or rejecting with error
 * @memberof CloudQuote
 */
export function getStockPriceHistory(params) {
	if (typeof params === 'string') { // passing in a string will treat that string as a symbol
		params = { symbol: params };
	}

	return apiCall('/fcon/getStockPriceHistory.json', params);
}

/**
 * Get minute-by-minute stock data
 * @function getStockTickHistory
 * @param {Object} params                      an object with parameters to be passed to the CloudQuote API
 * @param {String|String[]} [params.symbol]    a symbol or array of symbols
 * @param {String} [params.from]               a ISOString date to begin requesting data on
 * @param {String} [params.to]                 the end date to request data from, in ISOString format
 * @returns {Promise}                          a Promise resolving with JSON or rejecting with error
 * @memberof CloudQuote
 */
export function getStockTickHistory(params) {
	return apiCall('/fcon/getStockTickHistory.json', params);
}

/**
 * Get a list of sorted securities
 * @function sortedSecurityList
 * @param {Object} params     an object with parameters to be passed to the CloudQuote API
 * @returns {Promise}         a Promise resolving with JSON or rejecting with error
 * @memberof CloudQuote
 */
export function sortedSecurityList(params) {
	return apiCall('/fcon/sortedSecurityList.json', params)
		.then(symbolCacheStoreResult);
}

/**
 * Get a list of leading securities with pricing for the current trading session
 * @function getLeaders
 * @param {Object} params     an object with parameters to be passed to the CloudQuote API
 * @param {String} [params.list]    The list of securities to search (MOSTACTIVE, PERCENTGAINERS, PERCENTLOSERS, POPULAR)
 * @param {String} [params.type]    The type of security (STOCK,FUND,etc.)
 * @param {Integer} [params.limit]   The number of securities to return
 * @param {Bool} [params.calcChange]   The number of securities to return
 * @param {Number} [params.minPrice]   Minimum price of securities to include
 * @returns {Promise}         a Promise resolving with JSON or rejecting with error
 * @memberof CloudQuote
 */
export function getLeaders(params) {
	return apiCall('/fcon/getLeaders.json', params)
		.then(symbolCacheStoreResult);
}

/**
 * Returns calculated statistics about a security
 * @function getSecurityStats
 * @param {String} symbol    a symbol
 * @returns {Promise}        a Promise resolving with JSON or rejecting with error
 * @memberof CloudQuote
 */
export function getSecurityStats(symbol) {
	return apiCall('/fcon/getSecurityStats.json', { symbol });
}

/**
 * Retrieve all the stock splits for a security
 * @function getSplitsBySecurity
 * @param {String} symbol    a symbol
 * @returns {Promise}        a Promise resolving with JSON or rejecting with error
 * @memberof CloudQuote
 */
export function getSplitsBySecurity(symbol) {
	return apiCall('/fcon/getSplitsBySecurity.json', { symbol });
}

/**
 * Get financial data profiles of multiple companies (business summary, location, industry name, sicSector etc)
 * @function getProfile
 * @param {String|String[]} symbols    a symbol or array of symbols
 * @returns {Promise}                  a Promise resolving with JSON or rejecting with error
 * @memberof CloudQuote
 */
export function getProfile(symbols) {
	return apiCall('/barchart/getProfile.json', { symbols });
}

/**
 * Retreive the option chain for a security
 * @function getEquityOptions
 * @param {String|String[]} symbols    a symbol or array of symbols
 * @returns {Promise}                  a Promise resolving with JSON or rejecting with error
 * @memberof CloudQuote
 */
export function getEquityOptions(symbols) {
	return apiCall('/barchart/getEquityOptions.json', { underlying_symbols: symbols });
}

/**
 * Retreive the earnings estimates for a security
 * @function getEarningsEstimates
 * @param {String|String[]} symbols    a symbol or array of symbols
 * @returns {Promise}                  a Promise resolving with JSON or rejecting with error
 * @memberof CloudQuote
 */
export function getEarningsEstimates(symbols) {
	return apiCall('/barchart/getEarningsEstimates.json', { symbols });
}

/**
 * Retreive detailed earnings estimates for a security
 * @function getEarningsEstimatesBySecurity
 * @param {Object|String} params       an object with parameters to be passed to the CloudQuote API. if passed a string, the string will be used as the only parameter "symbol"
 * @param {String} [params.symbol]     a symbol
 * @returns {Promise}                  a Promise resolving with JSON or rejecting with error
 * @memberof CloudQuote
 */
export function getEarningsEstimatesBySecurity(params) {
	if (typeof params === 'string') {
		params = { symbol: params };
	}
	return apiCall('/barchart/getEarningsEstimatesBySecurity.json', params);
}

/**
 * Retreive the corporate actions for a security
 * @function getCorporateActions
 * @param {Object} params                         an object with parameters to be passed to the CloudQuote API
 * @param {String|String[]} [params.symbols]      a symbol or array of symbols
 * @param {String} [params.eventType]             the event type to get, one of [ dividend, split, earnings ]
 * @returns {Promise}                             a Promise resolving with JSON or rejecting with error
 * @memberof CloudQuote
 */
export function getCorporateActions(params) {
	return apiCall('/barchart/getCorporateActions.json', params);
}

/**
 * Retreive the competitors for a security.
 * @function getCompetitors
 * @param {String} symbol    a symbol
 * @returns {Promise}        a Promise resolving with JSON or rejecting with error
 * @memberof CloudQuote
 */
export function getCompetitors(symbol) {
	return apiCall('/barchart/getCompetitors.json', { symbol });
}

/**
 * Get information and useful links about a company (name, website, logo, social media etc)
 * @function getCompanyProfile
 * @param {String} symbol    a symbol
 * @returns {Promise}        a Promise resolving with JSON or rejecting with error
 * @memberof CloudQuote
 */
export function getCompanyProfile(symbol) {
	return apiCall('/fcon/getCompanyProfile.json', { symbol });
}

/**
 * Get a URL to a company logo from a symbol
 * @function getCompanyLogoURL
 * @param {String} symbol   a symbol
 * @returns {String}        A URL pointing to the company logo
 * @memberof CloudQuote
 */
export function getCompanyLogoURL(symbol) {
	return getApiUrl('/fcon/getCompanyLogo.png', { symbol });
}

/**
 * Get a price chart in SVG format
 * @function getSVGPriceChartURL
 * @param {Object} params    an object with parameters to be passed to the CloudQuote API
 * @returns {String}         a URL to an SVG price chart
 * @memberof CloudQuote
 */
export function getSVGPriceChartURL(params) {
	return getApiUrl('/fcon/getPriceChart.svg', params);
}

/**
 * Get a price chart in PNG format
 * @function getPNGPriceChartURL
 * @param {Object} params    an object with parameters to be passed to the CloudQuote API
 * @returns {String}         a URL to a PNG price chart
 * @memberof CloudQuote
 */
export function getPNGPriceChartURL(params = {}) {
	if (window.devicePixelRatio > 2) {
		params.hidpi = 3;
	}
	else if (window.devicePixelRatio > 1) {
		params.hidpi = 2;
	}
	return getApiUrl('/fcon/getPriceChart.png', params);
}

/**
 * Retrieve a list of income statements for a chosen security
 * @function getIncomeStatement
 * @param {String} symbol    a symbol
 * @returns {Promise}        a Promise resolving with JSON or rejecting with error
 * @memberof CloudQuote
 */
export function getIncomeStatement(symbol) {
	return apiCall('/sage/getIncomeStatement.json', { symbol });
}

/**
 * Retreive a list of cash flow statements for a chosen security
 * @function getCashFlow
 * @param {String} symbol    a symbol
 * @returns {Promise}        a Promise resolving with JSON or rejecting with error
 * @memberof CloudQuote
 */
export function getCashFlow(symbol) {
	return apiCall('/sage/getCashFlow.json', { symbol });
}

/**
 * Retrieve a list of balance sheets for a chosen security
 * @function getBalanceSheet
 * @param {String} symbol    a symbol
 * @returns {Promise}        a Promise resolving with JSON or rejecting with error
 * @memberof CloudQuote
 */
export function getBalanceSheet(symbol) {
	return apiCall('/sage/getBalanceSheet.json', { symbol });
}

/**
 * Retrieve a list of insider activity for a chosen security
 * @function getInsiderActions
 * @param {Object} params                an object with parameters to be passed to the CloudQuote API
 * @param {String} [params.symbol]       symbol to get insider actions for
 * @param {Number} [params.count=5000]   number of actions to retrieve
 * @param {Number} [params.start=0]      the number of actions to skip before retrieving
 * @returns {Promise}                    a Promise resolving with JSON or rejecting with error
 * @memberof CloudQuote
 */
export function getInsiderActions(params) {
	return apiCall('/sage/getInsiderActions.json', params);
}

/**
 * Retrieve a list of institutions holding a chosen security
 * @function getInstitutionalHoldings
 * @param {Object} params                  an object with parameters to be passed to the CloudQuote API
 * @param {String} [params.symbol]         symbol to get institutional holdings for
 * @param {Number} [params.count=5000]     number of holdings to retrieve
 * @param {Number} [params.start=0]        the number of holdings to skip before retrieving
 * @returns {Promise}                      a Promise resolving with JSON or rejecting with error
 * @memberof CloudQuote
 */
export function getInstitutionalHoldings(params) {
	return apiCall('/sage/getInstitutionalHoldings.json', params);
}

/**
 * Returns all the rating events for all securities for a particular date range
 * @function getRatingsByDate
 * @param {Object} params                  an object with parameters to be passed to the CloudQuote API
 * @param {String} [params.startDate]      the start range to get dates, format YYYY-MM-DD
 * @param {String} [params.endDate]        the end range to get dates, format YYYY-MM-DD
 * @returns {Promise}                      a Promise resolving with JSON or rejecting with error
 * @memberof CloudQuote
 */
export function getRatingsByDate(params) {
	return apiCall('/streetinsider/getRatingsByDate.json', params);
}

/**
 * Returns all the rating events for a particular security
 * @function getRatingsBySecurity
 * @param {String} symbol    a symbol
 * @returns {Promise}        a Promise resolving with JSON or rejecting with error
 * @memberof CloudQuote
 */
export function getRatingsBySecurity(symbol) {
	return apiCall('/streetinsider/getRatingsBySecurity.json', { symbol });
}

/**
 * Request the most recent headlines from a content provider. Retrieves a list of articles and indexed metadata associated with those articles
 * @function getNewsHeadlines
 * @param {Object} params                  an object with parameters to be passed to the CloudQuote API
 * @param {Number} [params.provider.id]    The numeric ID of the provider you wish to retrieve news releases form (required)
 * @param {String} [params.startDate]      start date to get headlines from, ISO8601 date string
 * @param {String} [params.endDate]        end date to get headlines from, ISO8601 date string
 * @returns {Promise}                      a Promise resolving with JSON or rejecting with error
 * @memberof CloudQuote
 */
export function getNewsHeadlines(params) {
	return apiCall('/prconnect/getNewsHeadlines.json', params);
}

/**
 * Returns the unindexed metadata for an article, and renders the article from the content provider native format into a generic HTML format
 * @function renderNews
 * @param {Object} params         an object with parameters to be passed to the CloudQuote API
 * @param {Number} [params.id]    The numeric ID of the article you wish to render (required) (as found in getNewsHeadlines response)
 * @returns {Promise}             a Promise resolving with JSON or rejecting with error
 * @memberof CloudQuote
 */
export function renderNews(params) {
	return apiCall('/prconnect/renderNews.json', params);
}

/**
 * A function which can be streamed by the stockAPIStreamer
 * @property {Function} getCurrencyPrice
 * @property {Function} getStockPrice
 * @property {Function} getStockPriceHistory
 * @property {Function} getStockTickHistory
 */
const streamable = {
	getCurrencyPrice,
	getStockPrice,
	getStockPriceHistory,
	getStockTickHistory
};

/**
 * Returns an object that will continously emit new data for given stock symbols
 * @function stockAPIStreamer
 * @param {String|String[]} symbols    a symbol or list of symbols to continously fetch data for
 * @param {streamable} pollingFn       the name of a function to poll the API with, must be a key in {@link streamable}
 * @param {int} timeout                the interval timeout between successive calls to the API
 * @returns {streamer}                 returns a streamer object
 * @memberof CloudQuote
 * @example <caption>A streamer for Stock Prices of the GOOG symbol</caption>
 * let streamer = stockAPIStreamer('GOOG', 'getStockPrice').on('data', (data) => console.log('Got:', data));
 *
 */
export function stockAPIStreamer(symbols, pollingFn, timeout = 60000) {
	return newStockAPIStreamer({ symbols, pollingFn, timeout });
}

/**
 * Returns an object that will continously emit new data for given stock symbols
 * @function newStockAPIStreamer
 * @param {String|String[]} symbols    a symbol or list of symbols to continously fetch data for
 * @param {streamable} pollingFn       the name of a function to poll the API with, must be a key in {@link streamable}
 * @param {int} timeout                the interval timeout between successive calls to the API
 * @param {function} shouldUpdate      a function that can be evaluated each interval to determine whether or not to allow the api call to proceed
 * @returns {streamer}                 returns a streamer object
 * @memberof CloudQuote
 * @example <caption>A streamer for Stock Prices of the GOOG symbol</caption>
 * let streamer = stockAPIStreamer('GOOG', 'getStockPrice').on('data', (data) => console.log('Got:', data));
 *
 */
export function newStockAPIStreamer({ symbols, pollingFn, timeout, shouldUpdate }) {

	/**
	 * An object to stream API data for a list of symbols. Use {@link #CloudQuotestockAPIStreamer|CloudQuote.stockAPIStreamer} to create a streamer.
	 * @namespace streamer
	 */
	pollingFn = streamable[pollingFn];
	timeout = timeout || 60000;
	shouldUpdate = (typeof shouldUpdate === 'function') ? shouldUpdate : () => true;
	let streamer = {};
	let callbacks = { then: () => {}, catch: () => {} };
	let timer;


	/**
	 * Set an event handler for the streamer
	 * @function streamer.on
	 * @param {String} event         the event to listen for, valid events include [ 'data', 'error' ]
	 * @param {Function} callback    the callback to fire for the event
	 * @returns {streamer}           returns itself for chaining methods together
	 */
	streamer.on = (event, callback) => {
		if (event === 'data') callbacks.then = callback;
		if (event === 'error') callbacks.catch = callback;

		streamer.stream();
		return streamer;
	};

	/**
	 * Start/restart the streamer with a list of symbols
	 * @function streamer.stream
	 * @param {String|String[]} newSymbols    a list of symbols to continously get data for. if not passed any symbols, use the current symbols
	 * @returns {streamer}                    returns itself for chaining methods together
	 */
	streamer.stream = (newSymbols) => {
		streamer.end();
		symbols = newSymbols || symbols;
		if (symbols && symbols.length > 0) {
			pollingFn(symbols).then(callbacks.then).catch(callbacks.catch);

			timer = setInterval(() => {
				if (!shouldUpdate()) return;
				if (!timer) return;
				pollingFn(symbols).then(callbacks.then).catch(callbacks.catch);
			}, timeout);
		}
		return streamer;
	};

	/**
	 * Stop the streamer from fetching data
	 * @function streamer.end
	 * @returns {streamer}           returns itself for chaining methods together
	 */
	streamer.end = () => {
		clearInterval(timer);
		timer = null;
		return streamer;
	};

	/**
	 * Set the timeout between requests for the streamer
	 * @function streamer.setTimeout
	 * @param {Number} n             the timeout between polling the API in milliseconds
	 * @returns {streamer}           returns itself for chaining methods together
	 */
	streamer.setTimeout = (n) => {
		timeout = n;
		return streamer;
	};

	return streamer;
}

/**
 * Fields which can be provided to getDataTable
 * @constant DATA_TABLE_FIELDS
 * @param {Number} ANNUALDIVIDEND             dividend, in reporting currency
 * @param {Number} ASK                        ask price, in trading currency
 * @param {Number} ASKSIZE                    ask size, in shares
 * @param {Number} AVGVOLUME3M                3-month average daily volume, in shares
 * @param {Number} BID                        bid price, in trading currency
 * @param {Number} BIDSIZE                    bid size, in shares
 * @param {Number} BOOKVALUE                  book value, in reporting currency
 * @param {Number} CHANGEPCTVSHIGH52          percentage change vs. 52-week high
 * @param {Number} CHANGEPCTVSLOW52           percentage change vs. 52-week low
 * @param {Number} CHANGEPERCENT              share price percentage change vs. previous trading session, in trading currency
 * @param {Number} CHANGEPRICE                share price change vs. previous trading session, in trading currency
 * @param {Number} CHANGEVSHIGH52             change vs. 52-week high, in trading currency
 * @param {Number} CHANGEVSLOW52              change vs. 52-week low, in trading currency
 * @param {Number} CHGPCTVS200MA              percentage change vs. 200-day moving average price
 * @param {Number} CHGPCTVS50MA               percentage change vs. 50-day moving average price
 * @param {Number} CHGVS200MA                 change vs. 200-day moving average price, in trading currency
 * @param {Number} CHGVS50MA                  change vs. 50-day moving average price, in trading currency
 * @param {String} CURRENCY                   currency
 * @param {Number} EBITDA                     earnings before interest, taxes, depreciation & amortization, (trailing 12-month period?), in reporting currency
 * @param {Number} EPS                        earnings-per-share (trailing 12-month period?), in reporting currency
 * @param {Number} EPSESTCURYEAR              earnings-per-share estimate for the current fiscal year, in reporting currency
 * @param {Number} EPSESTNEXTQTR              earnings-per-share estimate for the current fiscal quarter, in reporting currency
 * @param {Number} EPSESTNEXTYEAR             earnings-per-share estimate for the next fiscal year, in reporting currency
 * @param {String} EXCHANGE                   exchange on which the security is traded
 * @param {Number} EXCHANGEDEFAULTCURRENCY    trading currency of the exchange on which the security is traded
 * @param {Number} EXCHANGESHORTNAME          short name of the exchange on which the security is traded
 * @param {Number} HIGH                       intra-day high, in trading currency
 * @param {Number} HIGH52                     52-week high, in trading currency
 * @param {Number} LASTDIVDATE                timestamp
 * @param {Number} LASTTRADETIME              timestamp in seconds
 * @param {Number} LOW                        intra-day low, in trading currency
 * @param {Number} LOW52                      52-week low, in trading currency
 * @param {Number} MARKETCAP                  market capitalization, in trading currency
 * @param {Number} MOVINGAVG200               200-day moving average, in trading currency
 * @param {Number} MOVINGAVG50                50-day moving average, in trading currency
 * @param {String} NAME                       company name
 * @param {Number} NOMINALCURRENCYCODE        code name of nomincal currency (e.g. 'USD')
 * @param {Number} OPEN                       opening price of the current or most recent trading session, in trading currency
 * @param {Number} PEESTCURYEAR               price-to-earnings ratio estimate for the current fiscal year
 * @param {Number} PEESTNEXTYR                price-to-earnings ratio estimate for the next fiscal year
 * @param {Number} PEG                        price-to-earnings-to-growth ratio
 * @param {Number} PREVCLOSE                  close price of the most recently-closed trading session, in trading currency
 * @param {Number} PRICE                      most recent trading price, in trading currency
 * @param {Number} PRICEBOOK                  price-to-book ratio
 * @param {Number} PRICESALES                 price-to-sales ratio (trailing 12-month period?)
 * @param {Number} SECURITYTYPECODE           code name of the security type (e.g. 'STOCK', 'FUND', etc.)
 * @param {Number} SHARESOUTSTANDING          number of shares outstanding
 * @param {String} SYMBOL                     security ticker or security ID
 * @param {Number} TRAILINGPE                 trailing price-to-earnings ratio
 * @param {Number} VOLUME                     cumulative trading volume for the current (or most recently closed?) session
 * @param {Number} YIELD                      trailing yield prercentage
 */
const DATA_TABLE_FIELDS = [
	'ANNUALDIVIDEND',
	'ASK',
	'ASKSIZE',
	'AVGVOLUME3M',
	'BID',
	'BIDSIZE',
	'BOOKVALUE',
	'CHANGEPCTVSHIGH52',
	'CHANGEPCTVSLOW52',
	'CHANGEPERCENT',
	'CHANGEPRICE',
	'CHANGEVSHIGH52',
	'CHANGEVSLOW52',
	'CHGPCTVS200MA',
	'CHGPCTVS50MA',
	'CHGVS200MA',
	'CHGVS50MA',
	'CURRENCY',
	'EBITDA',
	'EPS',
	'EPSESTCURYEAR',
	'EPSESTNEXTQTR',
	'EPSESTNEXTYEAR',
	'EXCHANGE',
	'EXCHANGEDEFAULTCURRENCY',
	'EXCHANGESHORTNAME',
	'HIGH',
	'HIGH52',
	'LASTDIVDATE',
	'LASTTRADETIME',
	'LOW',
	'LOW52',
	'MARKETCAP',
	'MOVINGAVG200',
	'MOVINGAVG50',
	'NAME',
	'NOMINALCURRENCYCODE',
	'OPEN',
	'PEESTCURYEAR',
	'PEESTNEXTYR',
	'PEG',
	'PREVCLOSE',
	'PRICE',
	'PRICEBOOK',
	'PRICESALES',
	'SECURITYTYPECODE',
	'SHARESOUTSTANDING',
	'SYMBOL',
	'TRAILINGPE',
	'VOLUME',
	'YIELD'
];

/**
 * Get values for a provided set of data fields for a provided list of securities
 * @function getDataTable
 * @param {Object} opts                                        options for the request
 * @param {String|String[]} opts.symbols                       a symbol or array of symbols
 * @param {String|String[]} [opts.fields=DATA_TABLE_FIELDS]    a field or array of fields
 * @param {Boolean} [opts.keyResponseDataBySymbol=false]       indicates whether the response should be mutated to be keyed by security
 * @returns {Promise}                                          a Promise resolving with JSON or rejecting with error
 * @memberof CloudQuote
 */
export function getDataTable(opts={}) {
	let { symbols, fields=DATA_TABLE_FIELDS } = opts;

	if (!symbols || !symbols.length) {
		return Promise.reject('No symbols provided.');
	}
	return apiCallWithManySymbols('/cloudquote/getDataTable.json', symbols, { fields: toCsv(fields) })
		.then(response => opts.keyResponseDataBySymbol ? keyResponseDataBySymbol(symbols, response): response);
}



// WEBPACK FOOTER //
// ../node_modules/@os/cloudquote/src/index.js